import React, { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { supabase } from '@/integrations/supabase/client';
import { Loader2 } from 'lucide-react';
import { LoginHistoryEntry } from '@/hooks/useSecurityDashboard';

interface LoginLocationMapProps {
  loginHistory: LoginHistoryEntry[];
}

const LoginLocationMap: React.FC<LoginLocationMapProps> = ({ loginHistory }) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const markers = useRef<mapboxgl.Marker[]>([]);
  const mapLoadedRef = useRef(false); // Fix closure bug in timeout
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  // Initialize map once
  useEffect(() => {
    let mounted = true;
    let loadTimeout: NodeJS.Timeout;

    const initializeMap = async () => {
      if (!mapContainer.current || map.current) return;

      try {
        console.log('🗺️ Initializing map...');
        console.log('Map container dimensions:', mapContainer.current?.clientWidth, 'x', mapContainer.current?.clientHeight);
        
        // Fetch Mapbox token from edge function
        const { data, error: tokenError } = await supabase.functions.invoke('get-mapbox-token');
        
        if (tokenError || !data?.token) {
          console.error('❌ Failed to fetch Mapbox token:', tokenError);
          setError('Unable to load map - token error');
          setLoading(false);
          return;
        }

        console.log('✅ Mapbox token retrieved successfully');

        if (!mounted) return;

        // Initialize Mapbox
        mapboxgl.accessToken = data.token;
        console.log('🌍 Creating Mapbox instance...');
        
        map.current = new mapboxgl.Map({
          container: mapContainer.current,
          style: 'mapbox://styles/mapbox/dark-v11',
          projection: 'globe' as any,
          zoom: 1.2,
          center: [0, 20],
          pitch: 0,
        });

        // Timeout fallback - use ref to avoid closure bug
        loadTimeout = setTimeout(() => {
          if (!mapLoadedRef.current && mounted) {
            console.error('⏱️ Map load timeout - map did not fire load event within 15 seconds');
            setError('Map took too long to load. Please refresh the page.');
            setLoading(false);
          }
        }, 15000);

        // Add navigation controls
        map.current.addControl(
          new mapboxgl.NavigationControl({
            visualizePitch: true,
          }),
          'top-right'
        );

        // Disable scroll zoom for smoother UX
        map.current.scrollZoom.disable();

        // Add atmosphere and fog effects
        map.current.on('style.load', () => {
          if (!map.current) return;
          console.log('Map style loaded');
          
          map.current.setFog({
            color: 'rgb(30, 30, 50)',
            'high-color': 'rgb(50, 50, 80)',
            'horizon-blend': 0.1,
          });
        });

        // Rotation animation settings
        const secondsPerRevolution = 180;
        const maxSpinZoom = 5;
        const slowSpinZoom = 3;
        let userInteracting = false;
        let spinEnabled = true;

        // Spin globe function
        function spinGlobe() {
          if (!map.current) return;
          
          const zoom = map.current.getZoom();
          if (spinEnabled && !userInteracting && zoom < maxSpinZoom) {
            let distancePerSecond = 360 / secondsPerRevolution;
            if (zoom > slowSpinZoom) {
              const zoomDif = (maxSpinZoom - zoom) / (maxSpinZoom - slowSpinZoom);
              distancePerSecond *= zoomDif;
            }
            const center = map.current.getCenter();
            center.lng -= distancePerSecond;
            map.current.easeTo({ center, duration: 1000, easing: (n) => n });
          }
        }

        // Event listeners for interaction
        map.current.on('mousedown', () => {
          userInteracting = true;
        });
        
        map.current.on('dragstart', () => {
          userInteracting = true;
        });
        
        map.current.on('mouseup', () => {
          userInteracting = false;
          spinGlobe();
        });
        
        map.current.on('touchend', () => {
          userInteracting = false;
          spinGlobe();
        });

        map.current.on('moveend', () => {
          spinGlobe();
        });

        // Handle Mapbox errors
        map.current.on('error', (e) => {
          console.error('❌ Mapbox error:', e.error);
          if (mounted) {
            setError(`Map error: ${e.error?.message || 'Unknown Mapbox error'}`);
            setLoading(false);
            clearTimeout(loadTimeout);
          }
        });

        // Wait for map to load
        map.current.on('load', () => {
          if (!mounted) return;
          console.log('✅ Map fully loaded and ready');
          mapLoadedRef.current = true; // Update ref to fix closure bug
          clearTimeout(loadTimeout);
          setMapLoaded(true);
          setLoading(false);
          spinGlobe();
        });

      } catch (err) {
        console.error('Map initialization error:', err);
        if (mounted) {
          setError('Failed to initialize map');
          setLoading(false);
        }
      }
    };

    initializeMap();

    // Cleanup
    return () => {
      mounted = false;
      clearTimeout(loadTimeout);
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, []);

  // Add/update markers when loginHistory changes
  useEffect(() => {
    if (!map.current || !mapLoaded) return;

    console.log(`Updating markers for ${loginHistory.length} login sessions`);

    // Remove existing markers
    markers.current.forEach(marker => marker.remove());
    markers.current = [];

    // Filter logins with valid coordinates
    const validLogins = loginHistory.filter(
      login => login.latitude !== null && login.longitude !== null
    );

    console.log(`Found ${validLogins.length} logins with coordinates`);

    // Add markers for each login location
    validLogins.forEach((login) => {
      if (!map.current || !login.latitude || !login.longitude) return;

      // Create marker element
      const el = document.createElement('div');
      el.className = 'login-marker';
      el.style.width = '20px';
      el.style.height = '20px';
      el.style.borderRadius = '50%';
      el.style.border = '2px solid white';
      el.style.cursor = 'pointer';
      el.style.boxShadow = '0 0 10px rgba(0,0,0,0.5)';
      el.style.transition = 'transform 0.2s';
      
      // Color-code by suspicion level with hardcoded colors
      if (login.is_suspicious) {
        el.style.backgroundColor = '#ef4444'; // red-500
        el.style.animation = 'pulse 2s infinite';
      } else {
        el.style.backgroundColor = '#3b82f6'; // blue-500
      }

      // Hover effect
      el.addEventListener('mouseenter', () => {
        el.style.transform = 'scale(1.3)';
      });
      el.addEventListener('mouseleave', () => {
        el.style.transform = 'scale(1)';
      });

      // Create popup
      const popup = new mapboxgl.Popup({ 
        offset: 25,
        closeButton: false,
        className: 'login-popup'
      }).setHTML(`
        <div style="padding: 8px; min-width: 200px;">
          <div style="font-weight: 600; margin-bottom: 4px; color: hsl(var(--foreground));">
            ${login.city}, ${login.country}
          </div>
          <div style="font-size: 12px; color: hsl(var(--muted-foreground)); margin-bottom: 4px;">
            ${new Date(login.login_time).toLocaleString()}
          </div>
          ${login.is_suspicious ? 
            '<div style="color: #ef4444; font-size: 12px; font-weight: 600;">⚠️ Suspicious Login</div>' : 
            '<div style="color: #3b82f6; font-size: 12px;">✓ Normal Login</div>'
          }
        </div>
      `);

      // Add marker to map
      const marker = new mapboxgl.Marker(el)
        .setLngLat([login.longitude, login.latitude])
        .setPopup(popup)
        .addTo(map.current);

      markers.current.push(marker);
    });
  }, [loginHistory, mapLoaded]);

  if (error) {
    return (
      <div className="relative w-full h-[400px] flex items-center justify-center bg-muted/20 rounded-lg border border-border">
        <p className="text-muted-foreground">{error}</p>
      </div>
    );
  }

  return (
    <div className="relative w-full h-[400px]">
      {loading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/80 rounded-lg">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      )}
      <div ref={mapContainer} className="absolute inset-0 rounded-lg shadow-lg" />
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        .mapboxgl-popup-content {
          background-color: hsl(var(--card));
          border: 1px solid hsl(var(--border));
          border-radius: 8px;
          padding: 0;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }
        .mapboxgl-popup-tip {
          border-top-color: hsl(var(--card));
        }
      `}</style>
    </div>
  );
};

export default LoginLocationMap;
